import moment, { type Moment } from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { useBoolean } from 'react-use'

import { getScheduleWindowTimesFromTruncatedFormat } from 'components/common/schedule-window-utils'
import {
  getActiveDaysOffset,
  shiftScheduleDayByOffset
} from 'components/services/settings/service-settings-page-utils'
import useAuth from 'hooks/use-auth'
import { type AttackTrigger } from 'types/api/schedules/active'
import {
  type RestrictedTimeWindowForDay,
  type RestrictedTimeWindows
} from 'types/api/teams'
import { type ScheduleDay } from 'types/schedules/schedules'
import { type Time24Hour } from 'types/time'
import { WEEK_DAYS } from 'utils/time/days'

type SerializedWindowForDay = {
  day: ScheduleDay
  window: RestrictedTimeWindowForDay
}

// pass in date time string & RestrictedTimeWindows[] list
// return isRestrictedTimeWindowActive and startDate and endDate if any match the specified time
export function checkForRestrictedTimeWindow(
  dateTime: string,
  restrictedTimeWindows: RestrictedTimeWindows[]
) {
  const targetDateTime = moment.utc(dateTime)
  const serializedWindowsForDay = serializeWindowsForDay(restrictedTimeWindows)
  const currentActiveRestrictedTimeWindow =
    getCurrentActiveRestrictedTimeWindow(
      targetDateTime,
      serializedWindowsForDay
    )

  if (currentActiveRestrictedTimeWindow) {
    const dayIndex = WEEK_DAYS.indexOf(currentActiveRestrictedTimeWindow.day)

    const startTime = moment
      .utc(currentActiveRestrictedTimeWindow.window.start, 'HH:mm')
      .day(dayIndex)
    const endTime = moment
      .utc(currentActiveRestrictedTimeWindow.window.end, 'HH:mm')
      .day(dayIndex)

    if (endTime.isBefore(startTime)) {
      endTime.add(1, 'day')
    }

    return {
      isRestrictedTimeWindowActive: true,
      windowStart: startTime.toDate(),
      windowEnd: endTime.toDate()
    }
  }

  return {
    isRestrictedTimeWindowActive: false,
    windowStart: null,
    windowEnd: null
  }
}

function serializeWindowsForDay(
  restrictedTimeWindows: RestrictedTimeWindows[]
): SerializedWindowForDay[] {
  return restrictedTimeWindows?.flatMap(
    ({ day_of_week: day, windows_for_day: windows }) =>
      windows.map((window) => ({ day, window }))
  )
}

function getMatchingWindowForAttackTrigger(
  attackTrigger: AttackTrigger,
  serializedWindowsForDay: SerializedWindowForDay[]
): SerializedWindowForDay | null {
  return (
    serializedWindowsForDay?.find((windowForDay) => {
      const { day, window } = windowForDay
      /*
       * We need to adjust the day index as we compare the attack trigger
       * active days against the restricted time window as we truncate the
       * date specificity prior to comparison.
       */
      const daysDelta = getActiveDaysOffset(
        attackTrigger.start,
        attackTrigger.timeZone ?? 'UTC'
      )

      // Converts truncated Time24Hour format into full Moment instances to use for comparison.
      const { start: windowStart, end: windowEnd } =
        getScheduleWindowTimesFromTruncatedFormat(window.start, window.end, day)

      // Loop through the activeDays list of the attackTrigger and find the first day that falls within a restricted time window
      const attackTriggerDay = attackTrigger.activeDays?.some((activeDay) => {
        // Shift the active day given the current selected timezone for normalization to UTC
        const shiftedDay = shiftScheduleDayByOffset(activeDay, -daysDelta)

        /*
         * Truncate any month/year specificity from our timestamps to ensure we are comparing moment
         * instances of the current week only. This ensures timestamps go through the same construction
         * logic as the restricted window.
         */
        const startTime24Hour = moment
          .utc(attackTrigger.start)
          .format('HH:mm') as Time24Hour
        const endTime24Hour = moment
          .utc(attackTrigger.end)
          .format('HH:mm') as Time24Hour

        // Re-construct the attack trigger's start and end times as Moment instances for the current active day.
        const { start: attackTriggerStart, end: attackTriggerEnd } =
          getScheduleWindowTimesFromTruncatedFormat(
            startTime24Hour,
            endTime24Hour,
            shiftedDay
          )

        // NOTE: the specificity of the time comparison is intentional (e.g. when using `isBefore` vs `isSameOrBefore`)
        return (
          // Make sure the start time falls within the window (non-inclusive for the window end time)
          (attackTriggerStart.isBefore(windowEnd) &&
            attackTriggerStart.isSameOrAfter(windowStart)) ||
          // Or otherwise check whether the end time falls within the window (non-inclusive for the window start time)
          (attackTriggerEnd.isAfter(windowStart) &&
            attackTriggerEnd.isSameOrBefore(windowEnd))
        )
      })

      return Boolean(attackTriggerDay)
    }) || null
  )
}

function getCurrentActiveRestrictedTimeWindow(
  targetDateTime: Moment,
  serializedWindowsForDay: SerializedWindowForDay[]
): SerializedWindowForDay | null {
  return (
    serializedWindowsForDay?.find((windowForDay) => {
      const { day, window } = windowForDay
      const dayIndex = WEEK_DAYS.indexOf(day)

      const startTime = moment.utc(window.start, 'HH:mm').day(dayIndex)
      const endTime = moment.utc(window.end, 'HH:mm').day(dayIndex)

      if (endTime.isBefore(startTime)) {
        endTime.add(1, 'day')
      }

      return targetDateTime.isBetween(startTime, endTime, undefined, '[]')
    }) || null
  )
}

export function useRestrictedTimeWindow() {
  const { currentTeam } = useAuth()

  const [isRestrictedTimeWindowActive, setIsRestrictedTimeWindowActive] =
    useBoolean(false)
  const [windowStart, setWindowStart] = useState<Moment | null>(null)
  const [windowEnd, setWindowEnd] = useState<Moment | null>(null)

  const restrictedTimeWindows = currentTeam?.prefs?.test_run_restriction_windows

  useEffect(() => {
    const timeWindow = checkForRestrictedTimeWindow(
      moment.utc().toISOString(),
      restrictedTimeWindows ?? []
    )

    if (
      isRestrictedTimeWindowActive !== timeWindow.isRestrictedTimeWindowActive
    ) {
      setIsRestrictedTimeWindowActive(timeWindow.isRestrictedTimeWindowActive)
    }
    setWindowEnd(
      timeWindow.windowEnd && moment.utc(timeWindow.windowEnd, 'HH:mm')
    )
    setWindowStart(
      timeWindow.windowStart && moment.utc(timeWindow.windowStart, 'HH:mm')
    )
  }, [
    isRestrictedTimeWindowActive,
    restrictedTimeWindows,
    setIsRestrictedTimeWindowActive
  ])

  const checkForRestrictedTimeWindowMemoized = useCallback(
    (dateTime: string) => {
      return checkForRestrictedTimeWindow(dateTime, restrictedTimeWindows ?? [])
    },
    [restrictedTimeWindows]
  )

  const checkForRestrictedTimeWindowForAttackTrigger = useCallback(
    (attackTrigger: AttackTrigger) => {
      const serializedWindowsForDay = serializeWindowsForDay(
        restrictedTimeWindows ?? []
      )

      return getMatchingWindowForAttackTrigger(
        attackTrigger,
        serializedWindowsForDay
      )
    },
    [restrictedTimeWindows]
  )

  return {
    isRestrictedTimeWindowActive,
    windowStart,
    windowEnd,
    checkForRestrictedTimeWindow: checkForRestrictedTimeWindowMemoized,
    checkAttackTrigger: checkForRestrictedTimeWindowForAttackTrigger
  }
}
